/** Shared date helpers for Vehicle Management overview & record filters. */

export function toYmdLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseVmDate(value) {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dateKeyFromRecord(record, fields = ["date"]) {
  for (const f of fields) {
    const d = parseVmDate(record?.[f]);
    if (d) return toYmdLocal(d);
  }
  return "";
}

export function defaultPeriodFilters(mode = "month") {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return {
    filterMode: mode,
    filterDay: toYmdLocal(now),
    filterMonth: `${y}-${m}`,
    filterYear: String(y),
    filterFrom: "",
    filterTo: "",
  };
}

/**
 * @param {string} ymd - yyyy-mm-dd
 * @param {'all'|'day'|'month'|'year'|'range'} mode
 * @param {{ filterDay?: string, filterMonth?: string, filterYear?: string, filterFrom?: string, filterTo?: string }} filters
 */
export function ymdInPeriod(ymd, mode, filters = {}) {
  if (mode === "all" || !ymd) return mode === "all" ? true : false;
  const d = parseVmDate(ymd);
  if (!d) return false;
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  if (mode === "day") {
    const want = filters.filterDay || toYmdLocal(new Date());
    return ymd === want;
  }
  if (mode === "month") {
    const pref = filters.filterMonth || defaultPeriodFilters("month").filterMonth;
    const [yy, mm] = pref.split("-").map(Number);
    return d.getFullYear() === yy && d.getMonth() === mm - 1;
  }
  if (mode === "year") {
    const yy = Number(filters.filterYear || new Date().getFullYear());
    return d.getFullYear() === yy;
  }
  if (mode === "range") {
    if (filters.filterFrom) {
      const [fy, fm, fd] = filters.filterFrom.split("-").map(Number);
      if (t < new Date(fy, fm - 1, fd).getTime()) return false;
    }
    if (filters.filterTo) {
      const [ty, tm, td] = filters.filterTo.split("-").map(Number);
      if (t > new Date(ty, tm - 1, td).getTime()) return false;
    }
    return true;
  }
  return true;
}

/** Expiry yyyy-mm-dd falls in the selected calendar month/year (for compliance view). */
export function expiryInPeriod(ymd, mode, filters = {}) {
  if (!ymd) return false;
  if (mode === "all") return true;
  return ymdInPeriod(ymd, mode === "day" ? "month" : mode, filters);
}

export function daysUntil(ymd) {
  if (!ymd) return 9999;
  const d = parseVmDate(ymd);
  if (!d) return 9999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.ceil((end - today) / 86400000);
}

export function docStatusLabel(ymd) {
  const dl = daysUntil(ymd);
  if (dl < 0) return "Expired";
  if (dl <= 30) return "Expiring Soon";
  if (dl < 9999) return "Valid";
  return "—";
}

export function addDaysYmd(ymd, days) {
  const d = parseVmDate(ymd);
  if (!d || !Number.isFinite(days)) return "";
  d.setDate(d.getDate() + days);
  return toYmdLocal(d);
}

export function periodLabel(mode, filters) {
  if (mode === "day") return filters.filterDay || "Today";
  if (mode === "month") {
    const pref = filters.filterMonth || "";
    if (!pref) return "Month";
    const [y, m] = pref.split("-");
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${names[Number(m) - 1] || m} ${y}`;
  }
  if (mode === "year") return filters.filterYear || "Year";
  if (mode === "range") {
    const a = filters.filterFrom || "…";
    const b = filters.filterTo || "…";
    return `${a} → ${b}`;
  }
  return "All time";
}
